import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listMappings, listPDFTemplates, listDataSources, deleteMapping, getMapping, createMapping, Mapping, PDFTemplate, DataSource } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useWizard } from '../context/WizardContext';
import './Overview.css';

const ProjectOverview: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete } = useAuth();
  const { updateState, resetState } = useWizard();
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [templates, setTemplates] = useState<PDFTemplate[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'updated'>('updated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mappingsData, templatesData, sourcesData] = await Promise.all([
        listMappings(),
        listPDFTemplates(),
        listDataSources(),
      ]);
      setMappings(Array.isArray(mappingsData) ? mappingsData : []);
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setDataSources(Array.isArray(sourcesData) ? sourcesData : []);
    } catch (err) {
      // Failed to load data - empty lists will be shown
    } finally {
      setLoading(false);
    }
  };

  const getTemplateName = (id: string) => {
    return templates.find((t) => t.id === id)?.name || 'Unbekannt';
  };

  const getDataSourceName = (id: string) => {
    return dataSources.find((ds) => ds.id === id)?.name || 'Unbekannt';
  };

  const handleGeneratePDF = (mappingId: string) => {
    navigate(`/generate?mapping=${mappingId}`);
  };

  const handleEditProject = (mappingId: string) => {
    navigate(`/projects/edit/${mappingId}`);
  };

  const handleDeleteProject = async (mappingId: string, mappingName: string) => {
    if (!window.confirm(`Möchten Sie das Projekt "${mappingName}" wirklich löschen?`)) {
      return;
    }

    try {
      await deleteMapping(mappingId);
      alert('Projekt erfolgreich gelöscht');
      loadData(); // Reload list
    } catch (err: any) {
      alert('Fehler beim Löschen: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCopyProject = async (mapping: Mapping) => {
    const newName = prompt('Name für die Kopie:', `${mapping.name} (Kopie)`);
    if (!newName) return;

    try {
      const fullMapping = await getMapping(mapping.id);
      await createMapping({
        name: newName,
        description: fullMapping.description,
        pdf_template_id: fullMapping.pdf_template_id,
        data_sources: fullMapping.data_sources || [],
        relationships: fullMapping.relationships || [],
        field_mappings: fullMapping.field_mappings || {},
        filename_pattern: fullMapping.filename_pattern,
      });
      alert('Projekt kopiert!');
      loadData();
    } catch (err: any) {
      alert('Fehler beim Kopieren: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleNewProject = () => {
    resetState(); // Clear any previous draft state
    navigate('/wizard/project');
  };

  const handleContinueDraft = async (mapping: Mapping) => {
    try {
      // Load full mapping data
      const fullMapping = await getMapping(mapping.id);

      // Populate wizard state with draft data
      updateState({
        mappingId: fullMapping.id,
        mappingName: fullMapping.name,
        mappingDescription: fullMapping.description || '',
        pdfTemplateId: fullMapping.pdf_template_id || null,
        dataSources: fullMapping.data_sources || [],
        relationships: fullMapping.relationships || [],
        fieldMappings: fullMapping.field_mappings || {},
        filenamePattern: fullMapping.filename_pattern || '',
        isDraft: true,
        currentStep: fullMapping.current_step || 1,
      });

      // Navigate to the appropriate step
      const step = fullMapping.current_step || 1;
      const stepRoutes: Record<number, string> = {
        1: '/wizard/project',
        2: '/wizard/template',
        3: '/wizard/datasources',
        4: '/wizard/relationships',
        5: '/wizard/mapping',
      };
      navigate(stepRoutes[step] || '/wizard/project');
    } catch (err: any) {
      alert('Fehler beim Laden des Entwurfs: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
  };

  const sortedMappings = [...mappings].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'name') {
      cmp = a.name.localeCompare(b.name, 'de');
    } else if (sortBy === 'updated') {
      cmp = new Date(a.updated_at || a.created_at).getTime() - new Date(b.updated_at || b.created_at).getTime();
    } else {
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  if (loading) {
    return (
      <div className="overview-container">
        <h1>Lade Projekte...</h1>
      </div>
    );
  }

  return (
    <div className="overview-container">
      <div className="overview-header">
        <div>
          <h1>Projekte</h1>
          <p className="overview-description">
            Alle erstellten Mapping-Projekte
          </p>
        </div>
        {canEdit && (
          <button onClick={handleNewProject} className="btn-primary">
            + Neues Projekt
          </button>
        )}
      </div>

      {mappings.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>Sortieren:</span>
          {([['name', 'Name'], ['updated', 'Zuletzt geändert'], ['created', 'Erstellt']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleSort(key)}
              className="btn-small"
              style={{
                background: sortBy === key ? '#006355' : '#e0e0e0',
                color: sortBy === key ? 'white' : '#333',
              }}
            >
              {label} {sortBy === key && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
          ))}
        </div>
      )}

      {mappings.length === 0 ? (
        <div className="empty-state">
          <h3>Keine Projekte vorhanden</h3>
          {canEdit ? (
            <>
              <p>Erstelle dein erstes Projekt, um loszulegen.</p>
              <button onClick={handleNewProject} className="btn-primary">
                Erstes Projekt erstellen
              </button>
            </>
          ) : (
            <p>Es wurden noch keine Projekte erstellt.</p>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {sortedMappings.map((mapping) => (
            <div
              key={mapping.id}
              className="card"
              onClick={() => mapping.is_draft ? handleContinueDraft(mapping) : handleGeneratePDF(mapping.id)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: 0, marginBottom: '0.5rem' }}>
                    {mapping.name}
                    {mapping.is_draft && (
                      <span
                        className="badge"
                        style={{ marginLeft: '0.75rem', background: '#ff9800', color: 'white', fontSize: '0.7rem' }}
                      >
                        ENTWURF
                      </span>
                    )}
                  </h2>
                  {mapping.description && (
                    <p style={{ color: '#666', marginBottom: '1rem' }}>{mapping.description}</p>
                  )}

                  <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <div>
                      <strong>📄 PDF-Formular:</strong>{' '}
                      <span style={{ color: '#666' }}>{getTemplateName(mapping.pdf_template_id)}</span>
                    </div>
                    <div>
                      <strong>📊 Datenvorlagen:</strong>{' '}
                      <span style={{ color: '#666' }}>
                        {mapping.data_sources?.map((ds) => getDataSourceName(ds.id)).join(', ') || 'Keine'}
                      </span>
                    </div>
                    <div>
                      <strong>🔗 Beziehungen:</strong>{' '}
                      <span style={{ color: '#666' }}>{mapping.relationships?.length || 0}</span>
                    </div>
                    <div>
                      <strong>📅 Erstellt:</strong>{' '}
                      <span style={{ color: '#666' }}>
                        {new Date(mapping.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>

                  {mapping.data_sources && mapping.data_sources.length > 0 && (
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {mapping.data_sources.map((ds) => {
                        const source = dataSources.find((s) => s.id === ds.id);
                        return (
                          <div key={ds.id} style={{ display: 'flex', gap: '0.25rem' }}>
                            <span className={`badge badge-${ds.type}`}>
                              {ds.type === 'master' ? 'Stammdaten' : 'Detaildaten'}
                            </span>
                            {ds.role === 'primary' && (
                              <span className="badge badge-primary">Primär</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '2rem' }} onClick={(e) => e.stopPropagation()}>
                  {mapping.is_draft ? (
                    <button
                      onClick={() => handleContinueDraft(mapping)}
                      className="btn-primary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      ✏️ Weiter bearbeiten
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEditProject(mapping.id)}
                      className="btn-primary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      ✏️ Bearbeiten
                    </button>
                  )}
                  {canEdit && !mapping.is_draft && (
                    <button
                      onClick={() => handleCopyProject(mapping)}
                      className="btn-small btn-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      📋 Kopieren
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteProject(mapping.id, mapping.name)}
                      className="btn-small btn-danger"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🗑️ Löschen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectOverview;
